import { yupResolver } from '@hookform/resolvers/yup';
import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useRouteMatch } from 'react-router';
import { toast } from 'sonner';
import * as yup from 'yup';
import { FormFieldType, IFormField, SurveyForm } from '../../../../../../../extViewmodels';
import { formatBirthdayString } from '../../../../../../../models/UiUtils';
import { computeVisibleFields, useConditionalVisibility } from '../../../../shared/hooks/useConditionalVisibility';
import {
	useClientCreateCustomSurveyResponseMutation,
	useClientGetCustomSurveyResponseByToken,
	useClientUpdateCustomSurveyResponseMutation,
} from '../../../../../../../queries';
import { useClientDeleteSurveyAttachmentByToken } from '../../../../../../../queries/Surveys/useClientDeleteSurveyAttachmentByToken';
import { Button } from '../../../../../../../web/components-v2/Button';
import { Checkbox } from '../../../../../../../web/components/Checkbox';
import { LoadingLogo } from '../../../../../../../web/components/LoadingLogo';
import { ISelectBoxOption, SelectBox } from '../../../../../../../web/components/SelectBox';
import { TextInput } from '../../../../../../../web/components/TextInput';
import { CountrySelector } from '../../../../../../../web/components/ValueSelectors/CountrySelector';
import { baseStyleSheet } from '../../../../../../../web/styles/styles';
import {
	ICustomSurvey,
	ICustomSurveyResponse,
	IOperationResultNoValue,
	IPublicFileAttachment,
} from '../../../../../../models/Api';
import { FileUpload } from '../../../../shared/FileUpload';
import { buildFileYupSchema } from '../../../../shared/FileUpload/utils';
import { styleSheet } from './styles';

type Props = {
	survey: ICustomSurvey;
	onResponseSubmitted?(response: ICustomSurveyResponse): void;
	surveyResponse: ICustomSurveyResponse;
	setSurveyResponse: React.Dispatch<React.SetStateAction<ICustomSurveyResponse>>;
	showCustomContent?: boolean;
};

interface IFormValues {
	email?: string;
	firstName?: string;
	lastName?: string;
	phoneNumber?: string;
	birthday?: string;
	address1?: string;
	address2?: string;
	city?: string;
	stateProvince?: string;
	postalCode?: string;
	country?: string;
	customProperties: Record<string, string | IPublicFileAttachment[]>;
}

const getDefaultValues = (survey: ICustomSurvey): IFormValues => {
	const customProperties: Record<string, string | IPublicFileAttachment[]> = {};
	survey.customForm?.fields.forEach(field => {
		if (field.fieldType === FormFieldType.FileUpload) {
			customProperties[field.id] = [] as IPublicFileAttachment[];
		} else {
			customProperties[field.id] = '';
		}
	});
	return {
		email: '',
		firstName: '',
		lastName: '',
		phoneNumber: '',
		birthday: '',
		address1: '',
		address2: '',
		city: '',
		stateProvince: '',
		postalCode: '',
		country: '',
		customProperties,
	};
};

export const ErrorMessage = ({ message }: { message: string }) => (
	<p className={`${css(styleSheet.errorMessage)} ${css(styleSheet.noMargin)}`}>{message}</p>
);

// Helper function to build custom properties schema
// surveyForm: when provided, visibility is computed at validation time from sibling field values
const buildCustomPropertiesSchema = (fields: IFormField<string>[], surveyForm?: SurveyForm) => {
	const schema: Record<string, yup.AnySchema> = {};

	fields.forEach(field => {
		if (!field.isHidden) {
			if (field.fieldType === FormFieldType.FileUpload) {
				schema[field.id] = buildFileYupSchema(field);
			} else {
				if (field.isOptional) {
					schema[field.id] = yup.string().optional();
				} else if (surveyForm?.surveyParts?.length) {
					// Compute visibility at validation time from sibling field values
					schema[field.id] = yup.string().test('required-if-visible', `${field.label} is required`, function (value) {
						// this.parent is the customProperties object containing all sibling values
						const visibleIds = computeVisibleFields(surveyForm, this.parent);
						if (!visibleIds.has(field.id)) {
							return true; // Field not visible → skip required check
						}
						return !!value && value.trim() !== '';
					});
				} else {
					schema[field.id] = yup.string().required(`${field.label} is required`);
				}
			}
		}
	});

	return yup.object().shape({
		...schema,
	});
};

// US phone number regex
const usPhoneRegExp = new RegExp(/^((\+1)?[\s-]?)?\(?[1-9]\d\d\)?[\s-]?[1-9]\d\d[\s-]?\d\d\d\d$/);

export const CustomSurveyResponseForm = ({
	survey,
	onResponseSubmitted,
	setSurveyResponse,
	surveyResponse,
	showCustomContent,
}: Props) => {
	const { params: urlParams = {} } = useRouteMatch<{
		token?: string;
	}>();

	const { data: recordedResponse } = useClientGetCustomSurveyResponseByToken({
		accessToken: urlParams.token,
		retry: false,
	});

	const deleteAttachmentMutation = useClientDeleteSurveyAttachmentByToken({});

	const [deletedAttachmentIds, setDeletedAttachmentIds] = React.useState<string[]>([]);

	const [loading, setLoading] = React.useState<boolean>(true);

	// Build validation schema based on survey configuration
	const validationSchema = React.useMemo(() => {
		const options = survey.options || {};
		return yup.object().shape({
			email:
				options.contactEmailOptions?.collect && options.contactEmailOptions?.require
					? yup.string().trim().email('Invalid email').required('Email is required')
					: yup.string(),
			firstName:
				options.contactNameOptions?.collect && options.contactNameOptions?.require
					? yup.string().required('First name is required')
					: yup.string(),
			lastName:
				options.contactNameOptions?.collect && options.contactNameOptions?.require
					? yup.string().required('Last name is required')
					: yup.string(),
			phoneNumber:
				options.contactPhoneNumberOptions?.collect && options.contactPhoneNumberOptions?.require
					? yup.string().trim().matches(usPhoneRegExp, 'Must be a valid phone number').required('Phone is required')
					: yup
							.string()
							.optional()
							.trim()
							.matches(usPhoneRegExp, { excludeEmptyString: true, message: 'Must be a valid phone number' }),
			birthday:
				options.contactBirthdayOptions?.collect && options.contactBirthdayOptions?.require
					? yup
							.string()
							.required('Birthday is required')
							.test('valid-date', 'Please enter a valid date (MM/DD/YYYY)', value => {
								if (!value) return false;
								const date = moment(value, 'MM/DD/YYYY', true);
								return date.isValid() && date.isBefore(moment()) && date.isAfter(moment().subtract(120, 'years'));
							})
					: yup
							.string()
							.optional()
							.test('valid-date', 'Please enter a valid date (MM/DD/YYYY)', value => {
								if (!value) return true; // Optional field, empty is valid
								const date = moment(value, 'MM/DD/YYYY', true);
								return date.isValid() && date.isBefore(moment()) && date.isAfter(moment().subtract(120, 'years'));
							}),
			address1:
				options.contactAddressOptions?.collect && options.contactAddressOptions?.require
					? yup.string().required('Address line 1 is required')
					: yup.string().optional(),
			address2: yup.string().optional(),
			city:
				options.contactAddressOptions?.collect && options.contactAddressOptions?.require
					? yup.string().required('City is required')
					: yup.string().optional(),
			stateProvince:
				options.contactAddressOptions?.collect && options.contactAddressOptions?.require
					? yup.string().required('State/Province is required')
					: yup.string().optional(),
			postalCode:
				options.contactAddressOptions?.collect && options.contactAddressOptions?.require
					? yup.string().required('Postal Code is required')
					: yup.string().optional(),
			country:
				options.contactAddressOptions?.collect && options.contactAddressOptions?.require
					? yup.string().required('Country is required')
					: yup.string().optional(),
			customProperties: survey?.customForm?.fields
				? buildCustomPropertiesSchema(
						survey.customForm.fields.map(field => ({
							...field,
							id: field.id || '', // Ensure 'id' is defined, provide default if necessary
						})),
						survey.surveyForm
					)
				: yup.object().shape({}),
		});
	}, [survey]);

	const { handleSubmit, control, formState, getValues, setValue, trigger } = useForm<IFormValues>({
		defaultValues: getDefaultValues(survey),
		resolver: yupResolver(validationSchema),
	});

	// Watch customProperties for reactive conditional visibility evaluation
	// useWatch triggers re-renders when any nested customProperties value changes
	const watchedCustomProperties = useWatch({ control, name: 'customProperties' });
	const visibleFieldIds = useConditionalVisibility(survey.surveyForm, watchedCustomProperties);

	// Clear values of fields that become hidden
	const prevVisibleRef = React.useRef<Set<string> | undefined>(undefined);
	React.useEffect(() => {
		const prev = prevVisibleRef.current;
		if (prev && visibleFieldIds) {
			for (const fieldId of prev) {
				if (!visibleFieldIds.has(fieldId)) {
					const field = survey.customForm?.fields.find(f => f.id === fieldId);
					if (field) {
						const defaultValue: string | IPublicFileAttachment[] =
							field.fieldType === FormFieldType.FileUpload ? [] : '';
						setValue(`customProperties.${fieldId}`, defaultValue);
					}
				}
			}
		}
		prevVisibleRef.current = visibleFieldIds;
	}, [visibleFieldIds, survey.customForm?.fields, setValue]);

	// Handle mutation success
	const handleMutationSuccess = async (data: ICustomSurveyResponse) => {
		toast.success('Survey response successfully submitted');
		setSurveyResponse(data);

		if (deletedAttachmentIds.length) {
			for (const attachmentId of deletedAttachmentIds) {
				await deleteAttachmentMutation.mutateAsync({
					attachmentId,
					accessToken: urlParams.token,
					surveyType: 'custom',
				});
			}
		}
		setDeletedAttachmentIds([]);
		if (onResponseSubmitted) {
			onResponseSubmitted(data);
		}
	};
	const introMessage = React.useMemo(() => {
		const text = survey.options?.intro?.document;
		if (!text) {
			return null;
		}
		// remove lev-content tag
		const textWithoutLevContent = text.replace(/<lev-content[^>]*>/g, '').replace(/<\/lev-content>/g, '');
		// remove all p tag styles
		const textWithoutPStyles = textWithoutLevContent.replace(/<p[^>]*>/g, '<p>').replace(/<\/p>/g, '</p>');
		return <div className={css(styleSheet.introContent)} dangerouslySetInnerHTML={{ __html: textWithoutPStyles }} />;
	}, [survey]);

	// Handle mutation error
	const handleMutationError = (error: IOperationResultNoValue) => {
		toast.error(error?.systemMessage || 'Error submitting survey response');
		console.error('Mutation error:', error);
	};

	// Real mutations instead of mock ones
	const createResponseMutation = useClientCreateCustomSurveyResponseMutation({
		onSuccess: handleMutationSuccess,
		onError: handleMutationError,
	});

	const updateResponseMutation = useClientUpdateCustomSurveyResponseMutation({
		onSuccess: handleMutationSuccess,
		onError: handleMutationError,
	});

	// Load existing response data if available
	const isMountedRef = React.useRef(true);

	React.useEffect(() => {
		isMountedRef.current = true;
		setLoading(true);

		const initializeForm = () => {
			// Check if component is still mounted before updating state
			if (!isMountedRef.current) return;

			// First try using the directly provided surveyResponse prop
			if (surveyResponse?.id) {
				// Populate form with existing response data
				setValue('email', surveyResponse.contactInfo?.email || '');
				setValue('firstName', surveyResponse.contactInfo?.firstName || '');
				setValue('lastName', surveyResponse.contactInfo?.lastName || '');
				setValue('phoneNumber', surveyResponse.contactInfo?.phoneNumber || '');
				setValue('birthday', formatBirthdayString(surveyResponse.contactInfo?.birthday || ''));
				setValue('address1', surveyResponse.contactInfo?.address?.address1 || '');
				setValue('address2', surveyResponse.contactInfo?.address?.address2 || '');
				setValue('city', surveyResponse.contactInfo?.address?.city || '');
				setValue('stateProvince', surveyResponse.contactInfo?.address?.stateProvince || '');
				setValue('postalCode', surveyResponse.contactInfo?.address?.postalCode || '');
				setValue('country', surveyResponse.contactInfo?.address?.country || '');

				// Set custom properties
				const customProperties = surveyResponse?.customProperties || {};
				if (customProperties) {
					Object.entries(customProperties).forEach(([key, value]) => {
						setValue(`customProperties.${key}`, value || '');
					});
				}
			}
			// Then try the fetched recordedResponse if available
			else if (recordedResponse) {
				// Populate form with existing response data
				setValue('email', recordedResponse.contactInfo?.email || '');
				setValue('firstName', recordedResponse.contactInfo?.firstName || '');
				setValue('lastName', recordedResponse.contactInfo?.lastName || '');
				setValue('phoneNumber', recordedResponse.contactInfo?.phoneNumber || '');
				setValue('birthday', formatBirthdayString(recordedResponse.contactInfo?.birthday || ''));
				setValue('address1', recordedResponse.contactInfo?.address?.address1 || '');
				setValue('address2', recordedResponse.contactInfo?.address?.address2 || '');
				setValue('city', recordedResponse.contactInfo?.address?.city || '');
				setValue('stateProvince', recordedResponse.contactInfo?.address?.stateProvince || '');
				setValue('postalCode', recordedResponse.contactInfo?.address?.postalCode || '');
				setValue('country', recordedResponse.contactInfo?.address?.country || '');

				// Set custom properties
				const customProperties = recordedResponse?.customProperties || {};
				if (customProperties) {
					Object.entries(customProperties).forEach(([key, value]) => {
						setValue(`customProperties.${key}`, value || '');
					});
				}
				setSurveyResponse(recordedResponse);
			}

			setLoading(false);
		};

		// Short timeout to ensure form is ready for values
		const timeoutId = setTimeout(initializeForm, 300);

		return () => {
			isMountedRef.current = false;
			clearTimeout(timeoutId);
		};
	}, [recordedResponse, surveyResponse, setValue, setSurveyResponse]);

	const handleSave = async () => {
		const isValid = await trigger();
		if (!isValid) {
			return;
		}
		const values = getValues();

		const fileFields = survey.customForm?.fields?.filter(f => f.fieldType === FormFieldType.FileUpload);
		const fileCustomProperties: Record<string, { fileName: string; attachmentId: string }[]> = {};
		Object.entries(values.customProperties).forEach(([key, value]) => {
			if (fileFields?.some(f => f.id === key)) {
				fileCustomProperties[key] = (value as IPublicFileAttachment[]).map(f => ({
					fileName: f.fileName,
					attachmentId: f.attachmentId,
				}));
			}
		});

		const body: ICustomSurveyResponse = {
			contactInfo: {
				email: values.email?.trim(),
				firstName: values.firstName?.trim(),
				lastName: values.lastName?.trim(),
				phoneNumber: values.phoneNumber?.trim(),
				birthday: values.birthday?.trim(),
				address: {
					address1: values.address1?.trim(),
					address2: values.address2?.trim(),
					city: values.city?.trim(),
					stateProvince: values.stateProvince?.trim(),
					postalCode: values.postalCode?.trim(),
					country: values.country?.trim(),
				},
			},
			customProperties: { ...values.customProperties, ...fileCustomProperties } as Record<
				string,
				string | IPublicFileAttachment[]
			>,
		};

		if (Object.values(values.customProperties).length) {
			const fieldsWithMissingInput = survey.customForm?.fields?.filter(
				x =>
					!x.isOptional &&
					!x.isHidden &&
					(visibleFieldIds === undefined || visibleFieldIds.has(x.id)) &&
					!values.customProperties[x.id]
			);
			if (fieldsWithMissingInput?.length) {
				toast.error(`Please fill out all required fields`);
				return;
			}
		}

		// PUT/Update if we have an id
		if (surveyResponse?.id) {
			const payload = {
				...surveyResponse,
				...body,
			};
			updateResponseMutation.mutate({
				accessToken: urlParams.token,
				body: payload,
			});
			return;
		}

		// POST/Create
		createResponseMutation.mutate({ token: urlParams.token, body });
	};

	if (loading || !survey) {
		return (
			<div className={css(styleSheet.loadingLogo)}>
				<LoadingLogo size={200} />
			</div>
		);
	}

	console.log(survey.customForm?.fields);
	return (
		<form className={css(styleSheet.surveyForm)} onSubmit={handleSubmit(handleSave)}>
			{showCustomContent && <h2 className={css(styleSheet.name)}>{survey.name}</h2>}
			{introMessage ?? null}
			<div className={css(styleSheet.formContainer)}>
				{survey.options?.contactNameOptions?.collect ? (
					<>
						<div className={css(styleSheet.formControl)}>
							<label htmlFor='client-firstName' className={css(styleSheet.label)}>
								First Name{' '}
								{survey.options.contactNameOptions.require ? (
									<span className={css(baseStyleSheet.required)}>*</span>
								) : null}
							</label>
							<Controller
								name='firstName'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<TextInput
										inputId='client-firstName'
										name='firstName'
										type='text'
										aria-label='First Name'
										{...fieldProps}
									/>
								)}
							/>
							{formState.isSubmitted && formState.errors.firstName && (
								<ErrorMessage message={formState.errors.firstName.message} />
							)}
						</div>

						<div className={css(styleSheet.formControl)}>
							<label htmlFor='client-lastName' className={css(styleSheet.label)}>
								Last Name{' '}
								{survey.options.contactNameOptions.require ? (
									<span className={css(baseStyleSheet.required)}>*</span>
								) : null}
							</label>
							<Controller
								name='lastName'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<TextInput inputId='client-lastName' name='lastName' type='text' {...fieldProps} />
								)}
							/>
							{formState.isSubmitted && formState.errors.lastName && (
								<ErrorMessage message={formState.errors.lastName.message} />
							)}
						</div>
					</>
				) : null}

				{survey.options?.contactEmailOptions?.collect ? (
					<div className={css(styleSheet.formControl)}>
						<label htmlFor='client-email' className={css(styleSheet.label)}>
							Email{' '}
							{survey?.options.contactEmailOptions.require ? (
								<span className={css(baseStyleSheet.required)}>*</span>
							) : null}
						</label>
						<Controller
							name='email'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<TextInput inputId='client-email' type='text' {...fieldProps} />
							)}
						/>
						{formState.isSubmitted && formState.errors.email && (
							<ErrorMessage message={formState.errors.email.message} />
						)}
					</div>
				) : null}
				{survey.options?.contactPhoneNumberOptions?.collect ? (
					<div className={css(styleSheet.formControl)}>
						<label htmlFor='client-phone' className={css(styleSheet.label)}>
							Phone{' '}
							{!survey?.options.contactPhoneNumberOptions.require ? (
								<small style={{ fontSize: 11 }}>(Optional)</small>
							) : (
								<span className={css(baseStyleSheet.required)}>*</span>
							)}
						</label>
						<Controller
							name='phoneNumber'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<TextInput inputId='client-phone' type='text' {...fieldProps} placeholder='(999)999-9999' />
							)}
						/>
						{formState.isSubmitted && formState.errors.phoneNumber && (
							<ErrorMessage message={formState.errors.phoneNumber.message} />
						)}
					</div>
				) : null}
				{survey.options?.contactBirthdayOptions?.collect ? (
					<div className={css(styleSheet.formControl)}>
						<label htmlFor='client-birthday' className={css(styleSheet.label)}>
							Birthday{' '}
							{survey?.options.contactBirthdayOptions.require ? (
								<span className={css(baseStyleSheet.required)}>*</span>
							) : (
								<small style={{ fontSize: 11 }}>(Optional)</small>
							)}
						</label>
						<Controller
							name='birthday'
							control={control}
							render={({ field: { ref, ...fieldProps } }) => (
								<TextInput
									inputId='client-birthday'
									type='text'
									{...fieldProps}
									placeholder='MM/DD/YYYY'
									maxLength={10}
								/>
							)}
						/>
						{formState.isSubmitted && formState.errors.birthday && (
							<ErrorMessage message={formState.errors.birthday.message} />
						)}
					</div>
				) : null}

				{survey.options?.contactAddressOptions?.collect ? (
					<div className={css(styleSheet.addressSection)}>
						<div className={css(styleSheet.formControl)}>
							<label htmlFor='client-address1' className={css(styleSheet.label)}>
								Address Line 1{' '}
								{survey?.options.contactAddressOptions.require ? (
									<span className={css(baseStyleSheet.required)}>*</span>
								) : (
									<small style={{ fontSize: 11 }}>(Optional)</small>
								)}
							</label>
							<Controller
								name='address1'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<TextInput inputId='client-address1' type='text' {...fieldProps} placeholder='Street Address' />
								)}
							/>
							{formState.isSubmitted && formState.errors.address1 && (
								<ErrorMessage message={formState.errors.address1.message} />
							)}
						</div>

						<div className={css(styleSheet.formControl)}>
							<label htmlFor='client-address2' className={css(styleSheet.label)}>
								Address Line 2 <small style={{ fontSize: 11 }}>(Optional)</small>
							</label>
							<Controller
								name='address2'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<TextInput
										inputId='client-address2'
										type='text'
										{...fieldProps}
										placeholder='Apartment, suite, etc.'
									/>
								)}
							/>
						</div>

						<div className={css(styleSheet.formControl)}>
							<div className={css(styleSheet.addressFieldRow)}>
								<div style={{ flex: 1 }}>
									<label htmlFor='client-city' className={css(styleSheet.label)}>
										City{' '}
										{survey?.options.contactAddressOptions.require ? (
											<span className={css(baseStyleSheet.required)}>*</span>
										) : (
											<small style={{ fontSize: 11 }}>(Optional)</small>
										)}
									</label>
									<Controller
										name='city'
										control={control}
										render={({ field: { ref, ...fieldProps } }) => (
											<TextInput inputId='client-city' type='text' {...fieldProps} placeholder='City' />
										)}
									/>
									{formState.isSubmitted && formState.errors.city && (
										<ErrorMessage message={formState.errors.city.message} />
									)}
								</div>
								<div style={{ flex: 1 }}>
									<label htmlFor='client-state' className={css(styleSheet.label)}>
										State/Province{' '}
										{survey?.options.contactAddressOptions.require ? (
											<span className={css(baseStyleSheet.required)}>*</span>
										) : (
											<small style={{ fontSize: 11 }}>(Optional)</small>
										)}
									</label>
									<Controller
										name='stateProvince'
										control={control}
										render={({ field: { ref, ...fieldProps } }) => (
											<TextInput inputId='client-state' type='text' {...fieldProps} placeholder='State/Province' />
										)}
									/>
									{formState.isSubmitted && formState.errors.stateProvince && (
										<ErrorMessage message={formState.errors.stateProvince.message} />
									)}
								</div>
								<div style={{ flex: 1 }}>
									<label htmlFor='client-postalCode' className={css(styleSheet.label)}>
										Postal Code{' '}
										{survey?.options.contactAddressOptions.require ? (
											<span className={css(baseStyleSheet.required)}>*</span>
										) : (
											<small style={{ fontSize: 11 }}>(Optional)</small>
										)}
									</label>
									<Controller
										name='postalCode'
										control={control}
										render={({ field: { ref, ...fieldProps } }) => (
											<TextInput inputId='client-postalCode' type='text' {...fieldProps} placeholder='Postal Code' />
										)}
									/>
									{formState.isSubmitted && formState.errors.postalCode && (
										<ErrorMessage message={formState.errors.postalCode.message} />
									)}
								</div>
							</div>
						</div>

						<div className={css(styleSheet.formControlFullWidth, styleSheet.countrySelectorContainer)}>
							<label id='client-country-label' className={css(styleSheet.label)}>
								Country{' '}
								{survey?.options.contactAddressOptions.require ? (
									<span className={css(baseStyleSheet.required)}>*</span>
								) : (
									<small style={{ fontSize: 11 }}>(Optional)</small>
								)}
							</label>
							<Controller
								name='country'
								control={control}
								render={({ field: { ref, ...fieldProps } }) => (
									<div
										role='combobox'
										aria-labelledby='client-country-label'
										aria-controls='country-options'
										aria-expanded='false'
									>
										<CountrySelector
											selectedValue={fieldProps.value}
											onSelectionChanged={selected => fieldProps.onChange(selected.value)}
											className={css(styleSheet.countrySelector)}
										/>
									</div>
								)}
							/>
							{formState.isSubmitted && formState.errors.country && (
								<ErrorMessage message={formState.errors.country.message} />
							)}
						</div>
					</div>
				) : null}

				{survey?.customForm?.fields
					?.filter(f => !f.isHidden && (visibleFieldIds === undefined || visibleFieldIds.has(f.id)))
					.map(f => {
						const fieldDropdownOptions = f.options?.map(o => {
							return {
								id: o.id,
								title: o.label,
								value: o.label,
							};
						});

						return (
							<div key={f.id} className={css(styleSheet.formControlFullWidth)}>
								<label htmlFor={f.id} className={css(styleSheet.label)}>
									{f.label} {!f.isOptional ? <span className={css(baseStyleSheet.required)}>*</span> : null}
								</label>
								<Controller
									name={`customProperties.${f.id}`}
									control={control}
									render={({ fieldState: { error }, field: { ref, ...fieldProps } }) => {
										if (f.fieldType === FormFieldType.String) {
											return (
												<>
													<TextInput
														type='text'
														inputId={f.id}
														inputClassName={css(styleSheet.textInput)}
														{...fieldProps}
														value={(fieldProps.value as string) || ''}
													/>
													{formState?.errors.customProperties?.[f.id] && (
														<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
													)}
												</>
											);
										}

										if (f.fieldType === FormFieldType.FileUpload) {
											return (
												<FileUpload
													token={urlParams.token}
													surveyType='custom'
													onFileSelected={files => {
														setValue(`customProperties.${f.id}`, files);
													}}
													files={(fieldProps.value as IPublicFileAttachment[]) ?? []}
													onDeleteFile={
														surveyResponse?.id
															? (file: IPublicFileAttachment) => {
																	setDeletedAttachmentIds(prev => [...prev, file.attachmentId]);
																}
															: undefined
													}
													error={error}
												/>
											);
										}

										if (f.fieldType === FormFieldType.Option) {
											const onSelectionChanged = (selectedOption: ISelectBoxOption<string>) => {
												setValue(`customProperties.${f.id}`, selectedOption.id);
											};
											const onRenderTriggerTitle = (selectedOption: ISelectBoxOption<string>) => {
												return <span>{selectedOption.title as React.ReactNode}</span>;
											};
											return (
												<>
													<SelectBox
														options={fieldDropdownOptions}
														onRenderSelectedOptionTitle={onRenderTriggerTitle}
														onSelectionChanged={onSelectionChanged}
														placeholder={<span>Select option</span>}
														triggerClassName={css(styleSheet.selectBoxTriggerButton)}
														{...fieldProps}
														selectedOption={fieldDropdownOptions?.find(x => x.id === fieldProps.value) || undefined}
													/>
													{formState?.errors.customProperties?.[f.id] && (
														<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
													)}
												</>
											);
										}

										if (f.fieldType === FormFieldType.MultipleOptions) {
											const fieldCheckboxOptions = f.options?.map(o => {
												const fieldCheckboxSelectedOptions: string[] = (fieldProps?.value as string)?.split(',') ?? [];
												/**
												 * Method to update the value of the field
												 * @param e {React.ChangeEvent<HTMLInputElement>}
												 * sets a {String} value of the field
												 */
												const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
													const newOptions = [...fieldCheckboxSelectedOptions];
													if (e.target.checked) {
														newOptions.push(o.id);
													} else {
														newOptions.splice(fieldCheckboxSelectedOptions.indexOf(o.id), 1);
													}

													setValue(`customProperties.${f.id}`, `${newOptions.join(',')}`);
												};
												/**
												 * Use the onChange method to update the value of the field
												 */
												fieldProps.onChange = onChange;
												return (
													<Checkbox
														key={o.id}
														id={o.id}
														className={css(styleSheet.checkbox)}
														checked={fieldCheckboxSelectedOptions?.includes(o.id)}
														{...fieldProps}
														value={fieldProps.value as string}
													>
														<span>{o.label}</span>
													</Checkbox>
												);
											});
											return (
												<>
													{fieldCheckboxOptions}
													{formState?.errors.customProperties?.[f.id] && (
														<ErrorMessage message={formState?.errors.customProperties[f.id]?.message} />
													)}
												</>
											);
										}

										return null;
									}}
								/>
							</div>
						);
					})}
			</div>

			<footer className={css(styleSheet.actions)}>
				<div className={css(styleSheet.divider)} />
				<div className={css(styleSheet.buttonContainer)}>
					<Button
						type='submit'
						variant='primary'
						disabled={createResponseMutation.isPending || updateResponseMutation.isPending}
						isLoading={createResponseMutation.isPending || updateResponseMutation.isPending}
					>
						{!surveyResponse?.id || surveyResponse?.isDraft ? 'Submit' : 'Update Response'}
					</Button>
				</div>
			</footer>
		</form>
	);
};
